import axios from "axios";
import { getUtmPayload } from "@/lib/utm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Attach admin token when present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("gm_admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Public
export const joinWaitlist = (payload) =>
  api.post("/waitlist", { ...getUtmPayload(), ...payload }).then((r) => r.data);
export const getWaitlistStats = (code) => api.get(`/waitlist/${code}`).then((r) => r.data);
export const lookupWaitlistByEmail = (email) => api.post("/waitlist/lookup", { email }).then((r) => r.data);
export const submitInvestor = (payload) => api.post("/investors", payload).then((r) => r.data);
export const getBlogPosts = () => api.get("/blog").then((r) => r.data);
export const getBlogPost = (slug) => api.get(`/blog/${slug}`).then((r) => r.data);
export const getLeaderboard = () => api.get("/leaderboard").then((r) => r.data);
export const getPublicStats = () => api.get("/public/stats").then((r) => r.data);
export const getLiveNumbers = () => api.get("/public/live-numbers").then((r) => r.data);
export const getPublicActivity = () => api.get("/public/activity").then((r) => r.data);
export const trackAB = (experiment, variant, event) =>
  api.post("/ab/track", { experiment, variant, event }).catch(() => {});
export const getABConfig = () => api.get("/ab/config").then((r) => r.data);
export const trackView = (path, event = "view") => {
  const u = getUtmPayload();
  return api.post("/analytics/track", {
    path,
    event,
    referrer: typeof document !== "undefined" ? document.referrer : "",
    utm_source: u.source,
    utm_medium: u.utm_medium,
    utm_campaign: u.utm_campaign,
    utm_content: u.utm_content,
    utm_term: u.utm_term,
  }).catch(() => {});
};
export const adminAnalytics = () => api.get("/admin/analytics").then((r) => r.data);
export const requestDeleteAccount = (payload) => api.post("/delete-account", payload).then((r) => r.data);
export const submitVenueRequest = (payload) => api.post("/venue-requests", payload).then((r) => r.data);
export const submitContact = (payload) =>
  api.post("/contact", { ...getUtmPayload(), ...payload }).then((r) => r.data);

// Admin — new endpoints from this session
export const adminTrafficSources = () => api.get("/admin/traffic-sources").then((r) => r.data);
export const adminCityDemand = () => api.get("/admin/city-demand").then((r) => r.data);

// Admin
export const adminLogin = (password) => api.post("/admin/login", { password }).then((r) => r.data);
export const adminMe = () => api.get("/admin/me").then((r) => r.data);
export const adminStats = () => api.get("/admin/stats").then((r) => r.data);
export const adminWaitlist = () => api.get("/admin/waitlist").then((r) => r.data);
export const adminInvestors = () => api.get("/admin/investors").then((r) => r.data);
export const adminAB = () => api.get("/admin/ab").then((r) => r.data);
export const adminDeletions = () => api.get("/admin/deletion-requests").then((r) => r.data);
export const adminGateLinks = () => api.get("/admin/gatelinks").then((r) => r.data);
export const adminCreateGateLink = (payload) => api.post("/admin/gatelinks", payload).then((r) => r.data);
export const adminDeleteGateLink = (id) => api.delete(`/admin/gatelinks/${id}`).then((r) => r.data);
export const adminExportUrl = (kind) => `${API}/admin/export/${kind}`;
export const adminVenueRequests = () => api.get("/admin/venue-requests").then((r) => r.data);
export const adminApproveVenue = (id) => api.post(`/admin/venue-requests/${id}/approve`).then((r) => r.data);
export const adminRejectVenue = (id) => api.post(`/admin/venue-requests/${id}/reject`).then((r) => r.data);
export const adminABLocks = () => api.get("/admin/ab/locks").then((r) => r.data);
export const adminSetABLock = (experiment, variant) => api.post("/admin/ab/lock", { experiment, variant }).then((r) => r.data);
export const adminBadges = () => api.get("/admin/badges").then((r) => r.data);
export const adminSetBadges = (email, badges) => api.post("/admin/badges", { email, badges }).then((r) => r.data);
export const adminAlerts = (limit = 30) => api.get("/admin/alerts", { params: { limit } }).then((r) => r.data);
export const adminAlertMarkRead = (id) => api.post("/admin/alerts/mark-read", { id }).then((r) => r.data);
export const adminEmailLog = (limit = 100) => api.get("/admin/email-log", { params: { limit } }).then((r) => r.data);
export const adminImpersonateVenue = (venueIdOrSlug) =>
  api.post(`/admin/venues/${encodeURIComponent(venueIdOrSlug)}/impersonate`).then((r) => r.data);
export const adminBetaHealthPulse = () => api.get("/admin/beta-health/pulse").then((r) => r.data);
export const adminBetaHealthFunnel = () => api.get("/admin/beta-health/funnel").then((r) => r.data);
export const adminBetaHealthVenues = () => api.get("/admin/beta-health/venues").then((r) => r.data);
export const adminBetaHealthRecentEvents = (limit = 50) =>
  api.get("/admin/beta-health/recent-events", { params: { limit } }).then((r) => r.data);
export const adminFounderPrintList = () => api.get("/admin/perks/founder-print").then((r) => r.data);
export const adminFounderPrintMark = (email, shipped, shipping_address = null) =>
  api.post("/admin/perks/founder-print", { email, shipped, shipping_address }).then((r) => r.data);
export const adminDripStatus = () => api.get("/admin/drip/status").then((r) => r.data);
export const adminPreviewApproval = (rid) => api.get(`/admin/venue-requests/${rid}/preview`).then((r) => r.data);
export const adminSendDigestsNow = () => api.post("/admin/digests/send-now").then((r) => r.data);
export const adminVenueAnalytics = () => api.get("/admin/analytics/venues").then((r) => r.data);
export const adminContactMessages = () => api.get("/admin/contact-messages").then((r) => r.data);
export const adminUpdateContactStatus = (id, status) =>
  api.post(`/admin/contact-messages/${id}/status`, { status }).then((r) => r.data);

// ---- Venue portal ----------------------------------------------------------
const VENUE_TOKEN_KEY = "gm_venue_token";
export const venueSetToken = (t) => (t ? localStorage.setItem(VENUE_TOKEN_KEY, t) : localStorage.removeItem(VENUE_TOKEN_KEY));
export const venueGetToken = () => localStorage.getItem(VENUE_TOKEN_KEY);

const venueAuthHeader = () => {
  const t = venueGetToken();
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

export const venueLogin = (email, password) =>
  api.post("/venue/auth/login", { email, password }).then((r) => {
    if (r.data?.token) venueSetToken(r.data.token);
    return r.data;
  });
export const venueMe = () => api.get("/venue/me", venueAuthHeader()).then((r) => r.data);
export const venueDashboard = () => api.get("/venue/dashboard", venueAuthHeader()).then((r) => r.data);
export const venueUpdateProfile = (payload) => api.put("/venue/profile", payload, venueAuthHeader()).then((r) => r.data);
export const venueChangePassword = (current_password, new_password) =>
  api.post("/venue/auth/change-password", { current_password, new_password }, venueAuthHeader()).then((r) => r.data);
export const venueQrPosterUrl = () => `${API}/venue/qr-poster.pdf`;
export const venueOnboardingSeen = () =>
  api.post("/venue/onboarding/seen", {}, venueAuthHeader()).then((r) => r.data);
export const venueOnboardingChecklist = (changes) =>
  api.post("/venue/onboarding/checklist", changes, venueAuthHeader()).then((r) => r.data);
export const venueLogout = () => venueSetToken(null);

// Public venue directory
export const getVenueDirectory = () => api.get("/venue/directory").then((r) => r.data);
export const getVenueLive = (code) => api.get(`/venue/live/${code}`).then((r) => r.data);
